<template>
  <div v-if="shouldDropdownRender" class="box header-btn-hide-text">
    <FlotoDropdownPicker
      v-if="shouldDropdownRender"
      id="create-new-btn"
      :options="createOptions"
      placement="bottomRight"
      avoid-keyboard-navigationa
      @change="handleChange"
    >
      <template v-slot:trigger>
        <div @click.prevent.stop="$emit('click')">
          <div
            ref="buttonRef"
            class="create-button"
            role="button"
            tabindex="0"
            @keydown="handleKeyDown"
          >
            <MIcon name="plus" size="lg" />
          </div>
        </div>
      </template>
      <template
        v-if="createOptions.length"
        v-slot:menu-item="{ item, selectItem }"
      >
        <FlotoAnchorLink :id="item.key" @click="selectItem(item)">
          <div class="flex">
            <div class="mx-2">
              <MIcon :name="item.icon || 'question'" size="lg" />
            </div>
            <div>
              {{ item.name }}
            </div>
          </div>
        </FlotoAnchorLink>
      </template>
    </FlotoDropdownPicker>
  </div>
</template>

<script>
import { authComputed } from '@state/modules/auth'
import { LicenseComputed } from '@state/modules/license'
import { PreferenceComputed } from '@state/modules/preference'
import { SupportPortalConfigComputed } from '@state/modules/support-portal-config'
import { keyEvents } from '@utils/keyevents'

export default {
  name: 'CreateDropdown',
  props: { portalMode: { type: Boolean, default: false } },
  computed: {
    ...authComputed,
    ...LicenseComputed,
    ...PreferenceComputed,
    ...SupportPortalConfigComputed,
    shouldDropdownRender() {
      if (!this.createOptions.length) {
        return false
      }
      return this.portalMode ? this.loggedIn : true
    },
    isAllowedCreateIncident() {
      if (!this.allowRequesterToCreateIncidents && this.portalMode) {
        return false
      }
      return true
    },
    options() {
      const options = [
        ...(this.isAllowedCreateIncident
          ? [
              {
                key: 'request',
                icon: 'ticket-alt',
                name: `${this.$t('create')} ${this.$tc('incident', 1)}`,
                moduleKey: this.$constants.REQUEST,
                route: this.$modules.getModuleRoute('ticket', 'create', {
                  params: { ticketType: this.$constants.REQUEST },
                }),
                isModuleAvailable: (allAvailableModule) =>
                  allAvailableModule.indexOf(this.$constants.REQUEST) >= 0,
              },
            ]
          : []),
        {
          key: 'service_request',
          icon: 'request-service',
          name: `${this.$tc('request_service')}`,
          route: this.$modules.getModuleRoute('service-catalog', 'request'),
          moduleKey: this.$constants.REQUEST,
          isModuleAvailable: (allAvailableModule) =>
            allAvailableModule.indexOf(this.$constants.SERVICE_CATALOG) >= 0,
        },
        {
          key: 'problem',
          icon: 'problem',
          name: `${this.$t('create')} ${this.$tc('problem', 1)}`,
          moduleKey: this.$constants.PROBLEM,
          route: this.$modules.getModuleRoute('ticket', 'create', {
            params: { ticketType: this.$constants.PROBLEM },
          }),
          isModuleAvailable: (allAvailableModule) =>
            allAvailableModule.indexOf(this.$constants.PROBLEM) >= 0,
        },
        {
          key: 'change',
          icon: 'change',
          name: `${this.$t('create')} ${this.$tc('change', 1)}`,
          route: this.$modules.getModuleRoute('ticket', 'create', {
            params: { ticketType: this.$constants.CHANGE },
          }),
          moduleKey: this.$constants.CHANGE,
          isModuleAvailable: (allAvailableModule) =>
            allAvailableModule.indexOf(this.$constants.CHANGE) >= 0,
        },
        {
          key: 'release',
          icon: 'box-full',
          name: `${this.$t('create')} ${this.$tc('release', 1)}`,
          route: this.$modules.getModuleRoute('ticket', 'create', {
            params: { ticketType: this.$constants.RELEASE },
          }),
          moduleKey: this.$constants.RELEASE,
          isModuleAvailable: (allAvailableModule) =>
            allAvailableModule.indexOf(this.$constants.RELEASE) >= 0,
        },
        {
          key: 'asset',
          icon: 'laptop',
          name: `${this.$t('add')} ${this.$tc('asset', 1)}`,
          route: this.$modules.getModuleRoute('asset', 'create'),
          moduleKey: this.$constants.ASSET,
          isModuleAvailable: (allAvailableModule) =>
            allAvailableModule.indexOf(this.$constants.ASSET) >= 0,
        },
        {
          key: 'ci',
          icon: 'ci',
          name: `${this.$t('add')} ${this.$tc('ci', 1)}`,
          route: this.$modules.getModuleRoute('cmdb', 'create'),
          moduleKey: this.$constants.CMDB,
          isModuleAvailable: (allAvailableModule) =>
            allAvailableModule.indexOf(this.$constants.CMDB) >= 0,
        },
        {
          key: 'purchase',
          icon: 'shopping-cart',
          name: `${this.$tc('create')} ${this.$tc('purchase')} ${this.$tc(
            'order'
          )}`,
          route: this.$modules.getModuleRoute('purchase', 'create'),
          moduleKey: this.$constants.PURCHASE,
          isModuleAvailable: (allAvailableModule) =>
            allAvailableModule.indexOf(this.$constants.PURCHASE) >= 0,
        },
        {
          key: 'project',
          icon: 'project-diagram',
          name: `${this.$t('create')} ${this.$tc('project')}`,
          route: this.$modules.getModuleRoute('project', 'create'),
          moduleKey: this.$constants.PROJECT,
          isModuleAvailable: (allAvailableModule) =>
            allAvailableModule.indexOf(this.$constants.PROJECT) >= 0,
        },
        {
          key: 'contract',
          icon: 'handshake',
          name: `${this.$t('create')} ${this.$tc('contract')}`,
          route: this.$modules.getModuleRoute('contract', 'create'),
          moduleKey: this.$constants.CONTRACT,
          isModuleAvailable: (allAvailableModule) =>
            allAvailableModule.indexOf(this.$constants.CONTRACT) >= 0,
        },
      ]
      if (this.portalMode) {
        // update menu if the menu is for portal
        return options
          .filter((o) => {
            // Show project if user has project requester license
            if (o.key === 'project') {
              return this.hasProjectRequesterLicense
            }
            // Show other portal options
            return ['request', 'service_request'].indexOf(o.key) >= 0
          })
          .map((i) => ({
            ...i,
            route: this.$modules.getModuleRoute(
              'support-portal',
              i.key === 'service_request'
                ? 'service-list'
                : i.key === 'project'
                ? 'create-project'
                : 'create-request'
            ),
            isModuleAvailable: (allAvailableModule) =>
              allAvailableModule.indexOf(
                i.key === 'project'
                  ? this.$constants.PROJECT
                  : i.key === 'service_request'
                  ? this.allowRequesterToAccessServiceRequest
                    ? this.$constants.SERVICE_CATALOG
                    : null
                  : this.$constants.REQUEST
              ) >= 0,
            moduleKey: i.key !== this.$constants.PROJECT ? null : i.moduleKey,
          }))
      }
      return options
    },
    createOptions() {
      return this.options
        .filter((o) => {
          if (o.isModuleAvailable) {
            return o.isModuleAvailable(this.availableModulesInLicense)
          }
          return true
        })
        .filter((m) => {
          if (m.moduleKey) {
            return this.myAllowedModules.indexOf(m.moduleKey) >= 0
          }
          return true
        })
    },
  },
  methods: {
    handleKeyDown($event) {
      const buttonRef = this.$refs.buttonRef
      if ($event instanceof KeyboardEvent) {
        if ($event.key === keyEvents.SPACE) {
          $event.preventDefault()
          if (buttonRef) {
            buttonRef.click()
          }
        }
      }
    },
    handleChange(v) {
      const item = this.createOptions.find((o) => o.key === v)
      if (item.route) {
        this.$router.push(item.route)
      }
    },
  },
}
</script>

<style lang="less" scoped>
.create-button {
  @apply rounded;

  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 12px;
  color: var(--primary-button-text);
  cursor: pointer;
  background: var(--primary);
  border-color: var(--primary);
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:focus-visible {
    outline: 1px solid var(--primary);
    outline-offset: 2px;
  }

  // &:hover {
  //   color: var(--primary-button-hover-text);
  //   background: var(--primary-button-hover-bg);
  //   border-color: var(--primary-button-hover-bg);
  // }
}
</style>
